import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaSearch, FaTimes, FaUserPlus } from "react-icons/fa";
import { MdOutlineFileDownload } from "react-icons/md";
import { FaRegImage } from "react-icons/fa6";
import { FileUploader } from "react-drag-drop-files";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { BsSendFill } from "react-icons/bs";
import { useDispatch, useSelector } from "react-redux";
import { MdDelete } from "react-icons/md";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { fetchQ2m2DNLdata } from "../store/reducers/q2m2ApplicationsSlice";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Dropdown } from "react-bootstrap";
import { FaRegEdit } from "react-icons/fa";
import ReactPaginate from "react-paginate";
import { Button } from "@mui/material";
import Select from "react-select";
import FormPageHeader from "../admin/quizSystem/components/FormPageHeader";
import BusinessUnitLocation from "./BusinessUnitLocation";
import ActioncardsSet from "./ActioncardsSet";
import { resetBusinessUnitState } from "../store/reducers/businessUnitSlice";
import { setUserOrganizations } from "../store/reducers/userOrganizationsSlice";
import { setSelectedOrganization } from "../store/reducers/organizationSlice";

const convertToCSV = (data) => {
  const headers = [
    "ID",
    "Name",
    "first_name",
    "last_name",
    "email",
    "phone_number",
    "employee_id",
    "position",
    "unit_id",
    "Permissions",
  ];

  const csvRows = [
    headers.join(","),
    ...data.map((row) =>
      [
        row.id,
        row.name,
        row.first_name,
        row.last_name,
        row.email,
        row.phone_number,
        row.employee_id,
        row.position,
        row.unit_id,
        row.type,
      ]
        .map((field) => `"${field ?? ""}"`) // wrap in quotes and handle null
        .join(",")
    ),
  ];
  return csvRows.join("\n");
};

const convertDNLDataToCSV = (data) => {
  if (!data || data.length === 0) return "";
  const columns = [...new Set(data.flatMap((item) => Object.keys(item)))];

  const header = columns.join(",");
  const rows = data.map((row) =>
    columns
      .map((col) => {
        const val = row[col] ?? "";
        if (typeof val === "string") {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return `"${val}"`;
      })
      .join(",")
  );

  return [header, ...rows].join("\n");
};

const canAccessOwnerModulesForRole = (role) => {
  const normalizedRole = `${role || ""}`.toLowerCase().replace(/[\s_-]/g, "");

  return normalizedRole === "owner" || normalizedRole === "superadmin";
};

const canAccessAdminOwnerModulesForRole = (role) => {
  const normalizedRole = `${role || ""}`.toLowerCase().replace(/[\s_-]/g, "");

  return (
    normalizedRole === "admin" ||
    normalizedRole === "owner" ||
    normalizedRole === "superadmin"
  );
};

const canAccessLimitedAdministrationForRole = (role) => {
  const normalizedRole = `${role || ""}`.toLowerCase().replace(/[\s_-]/g, "");

  return (
    normalizedRole === "employee" || normalizedRole === "externalinstructor"
  );
};

const getBusinessUnitName = (item) =>
  item?.business_unit || item?.business_unit_name || item?.name || "";

const getLocationUnitName = (item) => item?.locations_units || "";

const normalizeBusinessUnitList = (payload) => {
  const data = payload?.data ?? payload;

  if (Array.isArray(data)) return data;

  return [];
};

const uniqueValues = (values) =>
  Array.from(
    new Set(
      values.filter(
        (value) => value !== undefined && value !== null && value !== ""
      )
    )
  );

const selectMenuStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 1200 }),
  menu: (base) => ({ ...base, zIndex: 1200 }),
};

const getEmployeeBusinessUnitValues = (item) => {
  const value = item?.business_units;

  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === "") return [];

  if (typeof value === "string" && value.includes(",")) {
    return value
      .split(",")
      .map((businessUnit) => businessUnit.trim())
      .filter(Boolean);
  }

  return [value];
};

const Administration = () => {
  const itemsPerPage = 10;
  const [t] = useTranslation();
  const history = useHistory();
  const [bulkData, setBulkData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [organizationRolesLoading, setOrganizationRolesLoading] =
    useState(true);
  const [downloadingFullData, setDownloadingFullData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const jwtToken = localStorage.getItem("jwt_token");
  const organizationId = localStorage.getItem("organisation_id");
  const userID = localStorage.getItem("user_id");
  const [file, setFile] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [businessUnitOptions, setBusinessUnitOptions] = useState([]);
  const [locationUnitOptions, setLocationUnitOptions] = useState([]);
  const [selectedBusinessUnitFilter, setSelectedBusinessUnitFilter] =
    useState(null);
  const [activeTab, setActiveTab] = useState(() =>
    canAccessAdminOwnerModulesForRole(localStorage.getItem("user_role")) ||
    canAccessAdminOwnerModulesForRole(localStorage.getItem("type"))
      ? "employees"
      : "business-units"
  );
  const userChangedTab = useRef(false);
  const dispatch = useDispatch();

  const handleChange = (files) => {
    setFile(files[0]);
  };

  const handleClearSelectedFile = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setFile(null);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [search]);

  const BulkData = (
    searchValue = "",
    page = currentPage,
    businessUnitFilter = selectedBusinessUnitFilter
  ) => {
    setLoading(true);

    let url = `${process.env.REACT_APP_API_URL}/organization-employee-list?id=${organizationId}&page=${page}&limit=${itemsPerPage}`;

    if (searchValue) {
      url += `&search=${encodeURIComponent(searchValue)}`;
    }

    if (businessUnitFilter?.value) {
      url += `&business_unit_id=${encodeURIComponent(
        businessUnitFilter.value
      )}`;
    }

    fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
    })
      .then(async (response) => {
        const data = await response.json();
        if (response.ok) {
          const responseData = data.data;
          const employees = Array.isArray(responseData)
            ? responseData
            : responseData?.employee || [];
          const total = Array.isArray(responseData)
            ? responseData.length
            : responseData?.total_count || 0;
          const totalPages = Array.isArray(responseData)
            ? Math.ceil(total / itemsPerPage)
            : responseData?.total_pages || Math.ceil(total / itemsPerPage);

          setBulkData(employees);
          setTotalCount(total);
          setPageCount(totalPages || 1);
        } else {
          toast.error(data.error);
        }
        setLoading(false);
      })
      .catch(() => {
        toast.error("Failed to fetch data");
        setLoading(false);
      });
  };

  useEffect(() => {
    if (activeTab === "employees") {
      BulkData(debouncedSearch, currentPage, selectedBusinessUnitFilter);
    }
  }, [debouncedSearch, currentPage, activeTab, selectedBusinessUnitFilter]);

  useEffect(() => {
    if (activeTab !== "employees" || !organizationId || !jwtToken) return;

    let shouldIgnore = false;

    Promise.all([
      fetch(
        `${process.env.REACT_APP_API_URL}/all-business-units-names/${organizationId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      ),
      fetch(
        `${process.env.REACT_APP_API_URL}/all-locations-units-names/${organizationId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      ),
    ])
      .then(async ([businessUnitResponse, locationResponse]) => {
        const businessUnitData = await businessUnitResponse.json();
        const locationData = await locationResponse.json();

        if (!businessUnitResponse.ok) {
          throw new Error(
            businessUnitData?.error || "Failed to fetch business units"
          );
        }

        if (!locationResponse.ok) {
          throw new Error(locationData?.error || "Failed to fetch locations");
        }

        if (!shouldIgnore) {
          setBusinessUnitOptions(
            normalizeBusinessUnitList(businessUnitData).map((item) => ({
              value: item.id,
              label: [`B${item.id}`, getBusinessUnitName(item)]
                .filter(Boolean)
                .join("-"),
              name: getBusinessUnitName(item),
            }))
          );
          setLocationUnitOptions(
            normalizeBusinessUnitList(locationData).map((item) => ({
              value: item.id,
              label: [`L${item.id}`, getLocationUnitName(item)]
                .filter(Boolean)
                .join("-"),
              name: getLocationUnitName(item),
            }))
          );
        }
      })
      .catch((error) => {
        if (!shouldIgnore) {
          toast.error(error.message || "Failed to fetch business unit data");
        }
      });

    return () => {
      shouldIgnore = true;
    };
  }, [activeTab, jwtToken, organizationId]);

  useEffect(() => {
    dispatch(fetchQ2m2DNLdata());
  }, [dispatch]);

  const { organizations } = useSelector((state) => state.userOrganizations);
  const q2m2_Dnl_Data = useSelector((state) => state.q2m2Applications.data);

  const businessUnitLabelById = useMemo(
    () =>
      businessUnitOptions.reduce((labelsById, option) => {
        labelsById[String(option.value)] = option.label || option.value;
        return labelsById;
      }, {}),
    [businessUnitOptions]
  );

  const locationUnitLabelById = useMemo(
    () =>
      locationUnitOptions.reduce((labelsById, option) => {
        labelsById[String(option.value)] = option.label || option.value;
        return labelsById;
      }, {}),
    [locationUnitOptions]
  );

  const getEmployeeBusinessUnitLocationPairs = (item) => {
    const value = item?.business_unit_locations;

    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === "") return [];

    try {
      const parsedValue = JSON.parse(value);
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch (error) {
      return [];
    }
  };

  const getEmployeeBusinessUnitLabels = (item) =>
    uniqueValues(
      getEmployeeBusinessUnitValues(item).map((businessUnit) => {
        if (businessUnit && typeof businessUnit === "object") {
          return (
            [getBusinessUnitName(businessUnit), businessUnit.location]
              .filter(Boolean)
              .join(" - ") || businessUnit.id
          );
        }

        if (businessUnit === undefined || businessUnit === null) {
          return "";
        }

        return businessUnitLabelById[String(businessUnit)] || businessUnit;
      })
    );

  const getEmployeeBusinessUnitLocationLabels = (item) =>
    uniqueValues(
      getEmployeeBusinessUnitLocationPairs(item).map((pair) => {
        const businessUnitId = pair?.business_unit;
        const locationUnitId = pair?.locations_units;
        const businessUnitLabel =
          businessUnitLabelById[String(businessUnitId)] ||
          (businessUnitId ? `B${businessUnitId}` : "");
        const locationLabel =
          locationUnitLabelById[String(locationUnitId)] ||
          (locationUnitId ? `L${locationUnitId}` : "");

        return [businessUnitLabel, locationLabel].filter(Boolean).join(" / ");
      })
    );

  const renderBusinessUnitCell = (item) => {
    const labels =
      getEmployeeBusinessUnitLocationLabels(item).length > 0
        ? getEmployeeBusinessUnitLocationLabels(item)
        : getEmployeeBusinessUnitLabels(item);

    if (!labels.length) {
      return <span>-</span>;
    }

    return (
      <div className="flex min-w-[220px] flex-wrap gap-1.5">
        {labels.map((label, index) => (
          <span
            key={`${label}-${index}`}
            className="inline-flex items-center rounded-md border border-admin-border bg-white px-2 py-1 text-xs font-medium text-admin-text shadow-sm"
          >
            {label}
          </span>
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (!jwtToken || organizations.length > 0) {
      setOrganizationRolesLoading(false);
      return;
    }

    const getConnectedOrganizations = async () => {
      setOrganizationRolesLoading(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/login-user-connected-in-organzation`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwtToken}`,
            },
          }
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data?.error || "Failed to fetch organization details"
          );
        }

        const connectedOrganizations = Array.isArray(data?.data)
          ? data.data
          : [];

        dispatch(setUserOrganizations(connectedOrganizations));

        const selectedOrg =
          connectedOrganizations.find(
            (org) =>
              String(org.organisation_id) === String(organizationId) &&
              String(org.user_role_id) ===
                String(localStorage.getItem("user_role_id"))
          ) ||
          connectedOrganizations.find(
            (org) => String(org.organisation_id) === String(organizationId)
          ) ||
          connectedOrganizations.find(
            (org) =>
              String(org.customer_id) ===
              String(localStorage.getItem("customer_id"))
          ) ||
          connectedOrganizations[0];

        if (selectedOrg) {
          localStorage.setItem("organisation_id", selectedOrg.organisation_id);
          localStorage.setItem("customer_id", selectedOrg.customer_id);
          localStorage.setItem("user_role_id", selectedOrg.user_role_id);
          localStorage.setItem("user_role", selectedOrg.role);

          dispatch(
            setSelectedOrganization({
              organisation_id: selectedOrg.organisation_id,
              customer_id: selectedOrg.customer_id,
              user_role_id: selectedOrg.user_role_id,
              user_role: selectedOrg.role,
            })
          );
        }
      } catch (error) {
        toast.error(error.message || "Something went wrong");
      } finally {
        setOrganizationRolesLoading(false);
      }
    };

    getConnectedOrganizations();
  }, [dispatch, jwtToken, organizationId, organizations.length]);

  const onSubmitHandler = () => {
    if (!file) {
      toast.error("Please select a file before submitting.");
      return;
    }
    setLoading(true);
    const bodyFormData = new FormData();
    bodyFormData.append("file", file);
    bodyFormData.append("organization_id", organizationId);

    fetch(`${process.env.REACT_APP_API_URL}/employee-list/upload-file`, {
      method: "POST",
      body: bodyFormData,
      headers: {
        Authorization: `Bearer ${jwtToken}`,
      },
    })
      .then(async (response) => {
        const data = await response.json();
        if (response.ok) {
          toast.success(data.message);
        }
        BulkData(debouncedSearch, currentPage);
        setLoading(false);
        return Promise.reject(data.error);
      })
      .catch((error) => {
        toast.error(error);
        setLoading(false);
      });
  };

  const downloadFullFileHandler = async () => {
    setDownloadingFullData(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/organization-employee-list?id=${organizationId}&page=1&limit=all`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
      });
      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || "Failed to fetch data");
        return;
      }

      const employees = Array.isArray(data.data)
        ? data.data
        : data.data?.employee || [];

      if (employees.length === 0) {
        toast.error("No data available to download.");
        return;
      }

      const csvData = convertToCSV(employees);
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
      const csvUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", csvUrl);
      link.setAttribute("download", "Action-cards current employee list.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(csvUrl);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setDownloadingFullData(false);
    }
  };

  const downloadDNLExcelHandler = () => {
    if (!q2m2_Dnl_Data || q2m2_Dnl_Data.length === 0) {
      toast.error("No data available to download.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(q2m2_Dnl_Data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Q2M2 DNL Data");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(data, "Q2M2 DNL Application Data.xlsx");
  };
  const downloadFullQ2M2DNLApplications = () => {
    if (q2m2_Dnl_Data.length === 0) {
      toast.error("No data available to download.");
      return;
    }

    const csvData = convertDNLDataToCSV(q2m2_Dnl_Data);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "Q2M2 DNL Application Data.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sendInvite = (email, user_id) => {
    fetch(`${process.env.REACT_APP_API_URL}/resend-welcome-email`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ email, user_id }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Invite sent successfully!");
        } else {
          toast.error(data.error || "Failed to send invite.");
        }
      })
      .catch((error) => {
        toast.error("Failed to send invite.");
      });
  };
  const handleDeleteClick = (item) => {
    if (item.id == userID) {
      toast.error("You can't remove your self.");
      return;
    }
    setDeleteUserId(item.id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteUserId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/remove-user-from-organisation/${deleteUserId}&${organizationId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
        }
      );
      if (res.ok) {
        toast.success("User remove successfully");
        setShowDeleteModal(false);
        setDeleteUserId(null);
        BulkData(debouncedSearch, currentPage);
      } else {
      }
    } catch (err) {
      toast.error("Error deleting user");
    }
    setLoading(false);
  };

  const isAdmin_Owner =
    canAccessAdminOwnerModulesForRole(localStorage.getItem("user_role")) ||
    canAccessAdminOwnerModulesForRole(localStorage.getItem("type")) ||
    organizations.some(
      (org) =>
        org.organisation_id === +localStorage.getItem("organisation_id") &&
        canAccessAdminOwnerModulesForRole(org.role)
    );
  const organizationRole = (organizations || []).find(
    (org) => org.organisation_id === +organizationId
  )?.role;
  const hasLocalAdministrationRole =
    canAccessAdminOwnerModulesForRole(localStorage.getItem("user_role")) ||
    canAccessAdminOwnerModulesForRole(localStorage.getItem("type")) ||
    canAccessLimitedAdministrationForRole(localStorage.getItem("user_role")) ||
    canAccessLimitedAdministrationForRole(localStorage.getItem("type"));
  const canResolveAdministrationAccess =
    hasLocalAdministrationRole ||
    (organizations || []).length > 0 ||
    !organizationRolesLoading;
  const hasLimitedAdministrationAccess =
    canAccessLimitedAdministrationForRole(localStorage.getItem("user_role")) ||
    canAccessLimitedAdministrationForRole(localStorage.getItem("type")) ||
    canAccessLimitedAdministrationForRole(organizationRole);
  const canAccessAdministration =
    isAdmin_Owner || hasLimitedAdministrationAccess;
  const canViewEmployeeTab = isAdmin_Owner;
  const canViewActioncardsSet =
    canAccessOwnerModulesForRole(localStorage.getItem("user_role")) ||
    canAccessOwnerModulesForRole(localStorage.getItem("type")) ||
    canAccessOwnerModulesForRole(organizationRole);

  // Redirect to home if the role cannot access administration.
  useEffect(() => {
    if (canResolveAdministrationAccess && !canAccessAdministration) {
      history.replace("/");
    }
  }, [canAccessAdministration, canResolveAdministrationAccess, history]);

  useEffect(() => {
    if (!canAccessAdministration || organizationRolesLoading) return;

    if (
      canViewEmployeeTab &&
      activeTab !== "employees" &&
      !userChangedTab.current
    ) {
      setActiveTab("employees");
      return;
    }

    if (!canViewEmployeeTab && activeTab === "employees") {
      setActiveTab("business-units");
    }
  }, [
    activeTab,
    canAccessAdministration,
    canViewEmployeeTab,
    organizationRolesLoading,
  ]);

  useEffect(() => {
    if (activeTab === "employees" && !canViewEmployeeTab) {
      dispatch(resetBusinessUnitState());
      setActiveTab("business-units");
      return;
    }

    if (activeTab === "actioncards-set" && !canViewActioncardsSet) {
      dispatch(resetBusinessUnitState());
      setActiveTab(canViewEmployeeTab ? "employees" : "business-units");
    }
  }, [activeTab, canViewActioncardsSet, canViewEmployeeTab, dispatch]);

  const handleTabChange = (tab) => {
    if (activeTab === tab) return;
    if (tab === "employees" && !canViewEmployeeTab) return;
    if (tab === "actioncards-set" && !canViewActioncardsSet) return;

    userChangedTab.current = true;
    dispatch(resetBusinessUnitState());
    setActiveTab(tab);
  };

  return (
    <div
      id="administration-root"
      className="admin-page-root min-h-[93vh] overflow-auto bg-white p-3 text-[#20232A] sm:p-4"
    >
      <FormPageHeader title={t("userMenuSidebar.label.Administration")} />

      <div className="mb-5 inline-flex max-w-full flex-wrap gap-1 rounded-xl border border-admin-border bg-[#f8fafc] p-1 shadow-sm">
        {canViewEmployeeTab && (
          <button
            type="button"
            className={`rounded-lg border-0 px-4 py-2.5 text-sm font-semibold transition-all ${
              activeTab === "employees"
                ? "bg-admin-primary text-white shadow-sm"
                : "bg-transparent text-admin-muted hover:bg-white hover:text-admin-primary"
            }`}
            onClick={() => handleTabChange("employees")}
          >
            Employee
          </button>
        )}
        <button
          type="button"
          className={`rounded-lg border-0 px-4 py-2.5 text-sm font-semibold transition-all ${
            activeTab === "business-units"
              ? "bg-admin-primary text-white shadow-sm"
              : "bg-transparent text-admin-muted hover:bg-white hover:text-admin-primary"
          }`}
          onClick={() => handleTabChange("business-units")}
        >
          Location & Business Unit
        </button>
        {canViewActioncardsSet && (
          <button
            type="button"
            className={`rounded-lg border-0 px-4 py-2.5 text-sm font-semibold transition-all ${
              activeTab === "actioncards-set"
                ? "bg-admin-primary text-white shadow-sm"
                : "bg-transparent text-admin-muted hover:bg-white hover:text-admin-primary"
            }`}
            onClick={() => handleTabChange("actioncards-set")}
          >
            Actioncards Set
          </button>
        )}
      </div>

      {activeTab === "employees" && canViewEmployeeTab && (
        <>
          {localStorage.getItem("type") == "Private" ? null : (
            <section className="mb-6 grid gap-6 rounded-[20px] border border-[#9CA3AF] bg-white p-4 md:p-6 lg:grid-cols-[minmax(0,1fr)_368px]">
              <div className="min-w-0">
                <h2 className="m-0 text-[22px] font-bold text-admin-text">
                  Upload Your File Here
                </h2>
                <div className="admin-upload-dropzone relative my-2 mb-2 flex min-h-[86px] w-full cursor-pointer items-center rounded-[14px] border-2 border-dashed border-blue-400 bg-white px-8 py-4">
                  <FaRegImage className="pointer-events-none mr-3 flex-shrink-0 text-[32px]" />

                  <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                    <span className="pointer-events-none min-w-0 truncate text-[18px] font-medium text-gray-800">
                      {file ? file.name : "Upload or drop a file right here"}
                      {!file && <span className="ml-1 text-gray-500">CSV</span>}
                    </span>

                    {file && (
                      <button
                        type="button"
                        className="relative z-[2] inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-admin-border bg-white text-admin-muted shadow-sm transition-all hover:border-admin-primary hover:text-admin-primary"
                        aria-label="Remove selected file"
                        onClick={handleClearSelectedFile}
                      >
                        <FaTimes size={14} />
                      </button>
                    )}
                  </div>

                  <div className="absolute inset-0 z-[1] cursor-pointer opacity-0">
                    <FileUploader
                      multiple
                      handleChange={handleChange}
                      types={["CSV"]}
                    />
                  </div>
                </div>
                <p className="mb-4 text-[13px] leading-[1.55] !text-[#F6605F] ">
                  *Please note that an email address is required for the CSV
                  file to be added to the database. Without a valid email, the
                  data will not be added. Furthermore, in the column
                  “Permissions” you can choose between “Admin”, “Employee”, or
                  “External Instructor”. Please ensure that these permission
                  levels are spelled correctly. Anything else will be classified
                  as “Employee” and given permission accordingly.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    className="admin-btn w-full no-underline sm:w-auto"
                    href="/assets/Documents/ActionCards dummy data.csv"
                    title="click to download"
                    download
                  >
                    <MdOutlineFileDownload aria-hidden="true" />
                    Download Demo file
                  </a>
                  {organizationId === "2" && (
                    <Dropdown className="admin-dropdown w-full sm:w-auto">
                      <Dropdown.Toggle id="dropdown-dnl-download">
                        <MdOutlineFileDownload aria-hidden="true" />
                        Download DNL Application
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={downloadFullQ2M2DNLApplications}
                        >
                          Download as CSV
                        </Dropdown.Item>
                        <Dropdown.Item onClick={downloadDNLExcelHandler}>
                          Download as Excel
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </div>
              </div>

              <aside className="flex flex-col gap-3 self-center rounded-[14px] border border-[#B8B8B8] p-4 shadow-sm lg:p-5">
                <Button className="admin-btn" onClick={downloadFullFileHandler}>
                  {downloadingFullData ? (
                    <>
                      <span>Downloading</span>
                      <span
                        className="spinner-border spinner-border-sm ml-2"
                        role="status"
                        aria-hidden="true"
                      />
                    </>
                  ) : (
                    <>
                      <MdOutlineFileDownload aria-hidden="true" />
                      Download Full Data
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  className="admin-btn w-full"
                  onClick={onSubmitHandler}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span>Submitting</span>
                      <span
                        className="spinner-border spinner-border-sm ml-2"
                        role="status"
                        aria-hidden="true"
                      />
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              </aside>
            </section>
          )}

          <section className="mb-6 rounded-2xl border border-admin-border bg-white p-4 shadow-admin md:p-6">
            <div className="mb-[18px] flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              {/* Left */}
              <div>
                <h2 className="m-0 text-[22px] font-extrabold text-admin-text">
                  {t("userMenuSidebar.label.EmployeeList")}
                </h2>
                <p className="mt-1 mb-0 text-sm text-admin-muted">
                  Showing {bulkData.length} of {totalCount} employees
                </p>
              </div>

              {/* Right Side */}
              <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
                <div className="w-full md:w-[280px]">
                  <Select
                    options={businessUnitOptions}
                    value={selectedBusinessUnitFilter}
                    onChange={(value) => {
                      setSelectedBusinessUnitFilter(value);
                      setCurrentPage(1);
                    }}
                    isClearable
                    menuPortalTarget={
                      typeof document !== "undefined"
                        ? document.body
                        : undefined
                    }
                    menuPosition="fixed"
                    styles={selectMenuStyles}
                    placeholder="Business unit - location"
                  />
                </div>
                <div className="flex min-h-[44px] w-full md:w-[320px] items-center gap-2.5 rounded-[10px] border border-gray-300 px-3">
                  <FaSearch className="text-gray-400" />

                  <input
                    type="text"
                    placeholder="Search employee"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="min-w-0 flex-1 border-0 bg-transparent outline-none"
                  />

                  {search && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearch("");
                        setCurrentPage(1);
                      }}
                      className="border-0 bg-transparent text-gray-400"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                <Link to="/administration/add" className="no-underline">
                  <button
                    type="button"
                    className="admin-btn w-full md:w-auto whitespace-nowrap"
                  >
                    <FaUserPlus />
                    Add User
                  </button>
                </Link>
              </div>
            </div>

            <div className="overflow-auto rounded-xl border border-admin-border">
              <table className="admin-data-table m-0 w-full min-w-[1180px]">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="sticky top-0 z-[1] h-14 whitespace-nowrap rounded-tl-xl bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                    >
                      ID
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                    >
                      First Name
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                    >
                      Last Name
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                    >
                      Email Address
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                    >
                      Phone Number
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                    >
                      Employee ID
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                    >
                      Position
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                    >
                      Business Unit
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                    >
                      Permission
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                    >
                      Action
                    </th>
                    {isAdmin_Owner && (
                      <th
                        scope="col"
                        className="sticky top-0 z-[1] h-14 whitespace-nowrap bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                      >
                        Send Invite
                      </th>
                    )}
                    {isAdmin_Owner && (
                      <th
                        scope="col"
                        className="sticky top-0 z-[1] h-14 whitespace-nowrap rounded-tr-xl bg-admin-primary px-4 py-3.5 text-left text-xs font-extrabold text-white"
                      >
                        Remove
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {bulkData.map((item) => {
                    return (
                      <tr
                        key={item.id}
                        className="transition-colors duration-200 even:bg-[#fafafa] hover:bg-[#fff3f4]"
                      >
                        <th
                          scope="row"
                          className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] font-normal text-admin-text align-middle"
                        >
                          {item.id}
                        </th>
                        <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                          {item.name}
                        </td>
                        <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                          {item.first_name}
                        </td>
                        <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                          {item.last_name}
                        </td>
                        <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                          {item.email}
                        </td>
                        <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                          {item.phone_number}
                        </td>
                        <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                          {item.employee_id}
                        </td>
                        <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                          {item.position}
                        </td>
                        <td className="whitespace-normal border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                          {renderBusinessUnitCell(item)}
                        </td>
                        <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                          {item.type}
                        </td>
                        <td className="whitespace-nowrap text-center border-b border-[#f0f0f0] px-2 py-2 text-[13px] text-admin-text align-middle">
                          <button
                            type="button"
                            className="inline-flex min-h-8 min-w-8 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-admin-primary transition-all duration-200 hover:-translate-y-px hover:text-admin-primary-hover"
                            aria-label="Edit employee"
                            onClick={() => {
                              history.push({
                                pathname: `/administration/update`,
                                search: `?id=${item.id}`,
                              });
                            }}
                          >
                            <FaRegEdit size={20} />
                          </button>
                        </td>
                        {isAdmin_Owner && (
                          <td className="whitespace-nowrap border-b text-center border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                            <button
                              type="button"
                              className="inline-flex min-h-8 min-w-8 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-admin-primary transition-all duration-200 hover:-translate-y-px hover:text-admin-primary-hover"
                              aria-label="Send invite"
                              onClick={() => {
                                sendInvite(item.email, item.id);
                              }}
                            >
                              <BsSendFill size={22} />
                            </button>
                          </td>
                        )}
                        {isAdmin_Owner && (
                          <td className="whitespace-nowrap border-b border-[#f0f0f0] px-4 py-3.5 text-[13px] text-admin-text align-middle">
                            <button
                              type="button"
                              className="inline-flex min-h-8 min-w-8 cursor-pointer items-center justify-center border-0 bg-transparent p-0 text-admin-danger transition-all duration-200 hover:-translate-y-px hover:text-red-600"
                              aria-label="Remove employee"
                              onClick={() => {
                                handleDeleteClick(item);
                              }}
                            >
                              <MdDelete size={24} />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}

                  <tr hidden={bulkData.length !== 0}>
                    <td
                      colSpan={isAdmin_Owner ? "13" : "11"}
                      className="px-4 py-8 text-center text-admin-muted"
                    >
                      Data Not Found
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {pageCount > 1 && (
              <div className="d-flex justify-content-end mt-3">
                <ReactPaginate
                  nextLabel="Next"
                  previousLabel="Previous"
                  onPageChange={(event) => setCurrentPage(event.selected + 1)}
                  pageCount={pageCount}
                  forcePage={currentPage - 1}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link"
                  nextClassName="page-item"
                  nextLinkClassName="page-link"
                  breakLabel="..."
                  breakClassName="page-item"
                  breakLinkClassName="page-link"
                  containerClassName="pagination flex-wrap"
                  activeClassName="active"
                  renderOnZeroPageCount={null}
                />
              </div>
            )}
          </section>
        </>
      )}

      {activeTab === "business-units" && <BusinessUnitLocation />}

      {activeTab === "actioncards-set" && canViewActioncardsSet && (
        <ActioncardsSet />
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove this user to this organization?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default Administration;
